/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { EntityVersion, ID, MetedataLinkDto, ReportStatusDto } from '@gh/common-shared-data';

import { AbstractRestService, arrayOf, identity, none, RestSettings, SlicedDataSource } from '@gh/core-data';
import { parametrizeUrl } from '@gh/core-util';

import { Observable } from 'rxjs';
import { ScheduleDetailsDto } from '../schedule-templates/schedule-template.dto';
import {
  ConstructionScheduleFilterDto,
  ConstructionScheduleListInitDto,
  ConstructionScheduleListItemDto,
  ConstructionWorksheetDto,
  JobProgressReportConfigDto,
  ScheduleJobProgressFilterDto,
  ScheduleSupervisorReportRequestDto,
  ScheduleSupervisorTaskDto,
  ScheduleSupervisorTaskFilterDto,
  ScheduleTaskDetailsDto,
  ScheduleVendorReportRequestDto,
  VendorReportInitDto,
} from './schedule.dto';
import {
  ConstructionScheduleListItemMapper,
  ConstructionWorksheetMapper,
  ScheduleDetailsMapper,
  ScheduleSupervisorReportRequestMapper,
  ScheduleSupervisorTaskFilterMapper,
  ScheduleTaskDetailsMapper,
  ScheduleVendorReportRequestMapper,
} from './schedule.mapper';
import { getPrintLink, PrintFilterDto } from '../reports';
import { map$ } from '@gh/rx/operators';
import { Confirmation } from '@gh/core-ui';

@Injectable()
export class ConstructionScheduleRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  initList(): Observable<ConstructionScheduleListInitDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/list/init`, identity());
  }

  findAllAsDataSource(filter: ConstructionScheduleFilterDto): SlicedDataSource<ConstructionScheduleListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/constructions`,
      filter,
      identity(),
      arrayOf(ConstructionScheduleListItemMapper));
  }

  findAllAsFinderDataSource(filter: ConstructionScheduleFilterDto): SlicedDataSource<ConstructionScheduleListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/constructions/finder`,
      filter,
      identity(),
      arrayOf(ConstructionScheduleListItemMapper));
  }

  // tslint:disable-next-line:max-line-length
  findAllSupervisorTasksAsDataSource(filter: ScheduleSupervisorTaskFilterDto): SlicedDataSource<ScheduleSupervisorTaskDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/constructions/supervisors-tasks`,
      filter,
      ScheduleSupervisorTaskFilterMapper,
      arrayOf(identity()));
  }

  submitSupervisorScheduleReport(filter: ScheduleSupervisorReportRequestDto): Observable<void> {
    return this.httpPost(
      `${this.bp}/schedules/constructions/supervisor-schedule-report`,
      filter,
      ScheduleSupervisorReportRequestMapper,
      none());
  }

  remove(scheduleId: ID, version: EntityVersion): Observable<void> {
    return this.httpDelete(
      parametrizeUrl(`${this.bp}/schedules/constructions/${scheduleId}`, { version }),
      void 0,
      none(),
      none());
  }

  init(jobId: ID, templateId: ID): Observable<ScheduleDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/init`, { jobId, templateId }, identity(), ScheduleDetailsMapper);
  }

  get(jobId: ID): Observable<ScheduleDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/${jobId}`, ScheduleDetailsMapper);
  }

  getCombined(jobIds: ID[]): Observable<ScheduleDetailsDto[]> {
    return this.httpGet(
      parametrizeUrl(`${this.bp}/schedules/constructions/combined-gantt-chart`, { id: jobIds }),
      arrayOf(ScheduleDetailsMapper));
  }

  create(schedule: ScheduleDetailsDto, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleDetailsDto> {
    let url = `${this.bp}/schedules/constructions`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPost(url, this.scheduleDtoToRequest(schedule), ScheduleDetailsMapper);
  }

  update(schedule: ScheduleDetailsDto, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleDetailsDto> {
    let url = `${this.bp}/schedules/constructions/${schedule.scheduleId}`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPut(url, this.scheduleDtoToRequest(schedule), ScheduleDetailsMapper);
  }

  recalculate(schedule: ScheduleDetailsDto): Observable<ScheduleDetailsDto> {
    return this.httpPut(`${this.bp}/schedules/constructions/recalculate`, schedule, ScheduleDetailsMapper);
  }

  getTask(scheduleId: ID, taskId: ID): Observable<ScheduleTaskDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/${scheduleId}/tasks/${taskId}`, ScheduleTaskDetailsMapper);
  }

  updateTask(scheduleId: ID, taskId: ID, task: ScheduleTaskDetailsDto, saveBaseDatesAnswer?: Confirmation):
    Observable<ScheduleTaskDetailsDto> {
    let url = `${this.bp}/schedules/constructions/${scheduleId}/tasks/${taskId}`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPut(url,
                        {...task, isInSupervisorSchedule: true },
                        ScheduleTaskDetailsMapper);
  }

  deliverJobProgressReport(filter: ScheduleJobProgressFilterDto): Observable<void> {
    return this.httpPost(
      `${this.bp}/schedules/constructions/job-progress-report/deliver`,
      filter,
      identity<ScheduleJobProgressFilterDto>(),
      none());
  }

  getJobProgressReportConfig(): Observable<JobProgressReportConfigDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/job-progress-report/init`, identity<JobProgressReportConfigDto>());
  }

  initVendorReport(): Observable<VendorReportInitDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/vendor-report`, identity());
  }

  deliverScheduleVendorReport(filter: ScheduleVendorReportRequestDto): Observable<void> {
    return this.httpPost(
      `${this.bp}/schedules/constructions/vendor-report`,
      filter,
      ScheduleVendorReportRequestMapper,
      none());
  }

  getWorksheet(scheduleId: ID): Observable<ConstructionWorksheetDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/${scheduleId}/worksheet`, ConstructionWorksheetMapper);
  }

  updateWorksheet(worksheet: ConstructionWorksheetDto): Observable<ConstructionWorksheetDto> {
    return this.httpPut(
      `${this.bp}/schedules/constructions/${worksheet.scheduleId}/worksheet`,
      worksheet,
      ConstructionWorksheetMapper);
  }

  getProduceStatus(task: number): Observable<ReportStatusDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/produce-status/${task}`, void 0, void 0, identity());
  }

  getS3Link(task: number): Observable<MetedataLinkDto> {
    return this.httpGet(`${this.bp}/schedules/constructions/complete-metadata/${task}`, identity());
  }

  print(filter: PrintFilterDto): Observable<string> {
    return this.httpPost(`${this.bp}/schedules/constructions/print`, filter, identity(), identity()).pipe(map$(getPrintLink));
  }

  private scheduleDtoToRequest(schedule: ScheduleDetailsDto): ScheduleDetailsDto {
    return {
      ...schedule,
      isSendReportToSupervisor: true,
      tasks: schedule.tasks.map((task) => ({
        ...task,
        isInSupervisorSchedule: true,
      })),
    };
  }
}
